import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const Carousel = ({ 
    items, 
    visibleSlides = 1, 
    infinite = false, 
    showNextPrevButtons = true,
    autoPlay = false, 
    autoPlayInterval = 5000
 }) => {
  const [index, setIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isClickDisabled, setIsClickDisabled] = useState(false);
  const [stopPlay, setStopPlay] = useState(false);
  const preventNextEnd = useRef(false);
  const hasDragged = useRef(false);



  const totalSlides = items?.length;
  const maxIndex = totalSlides - visibleSlides;

  const startX = useRef(0);
  const moveX = useRef(0);

  const resetValues = () => {
    startX.current = 0; // Reset startX to avoid incorrect calculations
    moveX.current = 0; // Reset moveX to avoid incorrect calculations
    setOffset(0); // Reset offset to avoid incorrect calculations   
    setDragging(false); // Reset dragging to avoid incorrect calculations
    preventNextEnd.current = false;
  }

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    resetValues();
    requestAnimationFrame(() => {
      setIndex((prev) => (prev < maxIndex ? prev + 1 : infinite ? 0 : prev));
    });
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    resetValues(); 
    requestAnimationFrame(() => {
      setIndex((prev) => (prev > 0 ? prev - 1 : infinite ? maxIndex : prev));
    });
  };

  const handleStart = (e) => {
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
    hasDragged.current = false;
    setDragging(true);
    setIsClickDisabled(false);
  };  

  const handleMove = (e) => {
    if (!dragging || preventNextEnd.current) return;
    moveX.current = e.touches ? e.touches[0].clientX : e.clientX;
    const movement = moveX.current - startX.current;
  
    // Mark that a real drag is happening
    if (Math.abs(movement) > 5) {
      hasDragged.current = true;
    }
  
    setOffset(movement);
  
    if (Math.abs(movement) > 20) {
      setIsClickDisabled(true);
    }
  };  

  const handleEnd = () => {
    if (preventNextEnd.current) {
      preventNextEnd.current = false;
      return;
    }
  
    if (!dragging) return;
  
    const movement = moveX.current - startX.current;
  
    // Only trigger slide if a real drag happened
    if (!hasDragged.current || Math.abs(movement) < 50) {
      resetValues();
      return;
    }
  
    setIsTransitioning(true);
    if (movement > 50) {
      prevSlide();
    } else if (movement < -50) {
      nextSlide();
    }
  
    resetValues();
  };  

  const autoPlayRef = useRef(null);
  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const startAutoPlay = () => {
    stopAutoPlay();
    autoPlayRef.current = setInterval(() => {
        nextSlide();
    }, autoPlayInterval);
  };

  useEffect(() => {
    if (autoPlay && !stopPlay && totalSlides > visibleSlides) {
      startAutoPlay();
    }

    return () => {
        stopAutoPlay();
    };
  }, [autoPlay, isTransitioning]);

  return (
    <div
      className="relative w-full overflow-x-clip select-none cursor-pointer z-10"
      style={{ touchAction: "pan-y" }}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onMouseDown={(e) => {
        setStopPlay(true);
        handleStart(e);
        e.preventDefault();
      }}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTransitionEnd={() => setIsTransitioning(false)}
    >
      <div
        className={`flex ease-out duration-500 ${isTransitioning ? "transition-transform" : ""}`}
        style={{
          transform: `translateX(calc(-${index * (100 / visibleSlides)}% + ${offset}px))`,
        }}
      >
        {items.map((item, idx) => (
          <div 
            key={idx} 
            className="flex-shrink-0 p-4" 
            style={{ width: `${100 / visibleSlides}%` }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isClickDisabled && typeof item?.onClick === "function") {
                item.onClick(e);
              }
            }}
          >
            {item?.children}
          </div>
        ))}
      </div>

      {showNextPrevButtons && totalSlides > visibleSlides && (
        <>
          <button
            onClick={(e) => { prevSlide(); e.stopPropagation(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-gray-700 text-white w-10 h-10 flex justify-center items-center rounded-full"
          >
            ←
          </button>
          <button
            onClick={(e) => { nextSlide(); e.stopPropagation(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-gray-700 text-white w-10 h-10 flex justify-center items-center rounded-full"
          >
            →
          </button>
        </>
      )}

      {totalSlides > visibleSlides && (
        <div className="flex justify-center mt-2 gap-2 items-center">
          {Array.from({ length: Math.ceil(totalSlides / visibleSlides) }).map((_, i) => (
            <button
              key={i}
              onClick={(e) => {
                preventNextEnd.current = true;
                setIndex(i * visibleSlides);
                e.stopPropagation();
                e.preventDefault();
              }}
              className={`rounded-full p7-semibold flex items-center justify-center ${
                Math.floor(index / visibleSlides) === i
                  ? "bg-primary-500 text-white font-bold text-lg w-6 h-6"
                  : "bg-gray-400 text-primary-100 w-3 h-3"
              }`}
            >
              {Math.floor(index / visibleSlides) === i ? i + 1 : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;


Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.node,
      onClick: PropTypes.func
    })
  ).isRequired,
  visibleSlides: PropTypes.number, // visible childrens in single slide
  infinite: PropTypes.bool, // loop
  showNextPrevButtons: PropTypes.bool, // moving buttons
  autoPlay: PropTypes.bool, 
  autoPlayInterval: PropTypes.number
};
